import { useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { elementColor } from '../elements';
import {
  VizCard,
  NowYouTry,
  VIZ_BTN_GHOST,
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../vizShared';

// SkeletalStructure — draws an organic molecule as a line-angle (skeletal)
// structure from an EXPLICIT spec. This fills a real gap in the widget set:
// nothing else can draw a carbon chain, a C=C / C≡C, or a functional group.
// We deliberately do NOT parse IUPAC names — the author hands us the exact
// backbone, bonds, branches and functional group, and we render the canonical
// skeletal convention (every vertex/endpoint = a carbon, hydrogens implicit).
//
// config: {
//   carbons?: number,                       // main-chain carbon count (default 6)
//   cyclic?: boolean,                       // draw carbons as a CLOSED RING (regular polygon)
//   bonds?: [{ between: number, order: 1|2|3 }],  // 1-indexed C n — C n+1
//                                           //   (in a ring, between=N closes carbon N → carbon 1)
//   substituents?: [{ at: number, group: string }], // branch on carbon n
//                                           //   (multiple entries with the same `at` SPLAY apart)
//   functional?: 'alcohol'|'ether'|'aldehyde'|'ketone'|'carboxylic-acid'|'ester'|'none',
//   functionalAt?: number,                  // carbon index for the group (default 1)
//   esterAlkyl?: string,                    // for functional:'ester' — off-O alkyl label (e.g. 'propyl')
//   formula?: string, name?: string, title?: string,
// }
// Empty config → a plain hexane zig-zag (6 carbons, all single bonds).
// cyclic:true with carbons:6 → a cyclohexane hexagon.
//
// Cue:
//   cue.progress  0..1 draws the skeleton left → right
//   cue.step      reveals one bond/vertex at a time
//   cue.highlight 'double' | 'triple' | 'functional' | a carbon index — spotlights
//   reduced motion → the complete static structure

export type FunctionalKind =
  | 'alcohol'
  | 'ether'
  | 'aldehyde'
  | 'ketone'
  | 'carboxylic-acid'
  | 'ester'
  | 'none';

export interface SkeletalStructureConfig {
  carbons?: number;
  cyclic?: boolean;
  bonds?: Array<{ between?: number; order?: 1 | 2 | 3 | number }>;
  substituents?: Array<{ at?: number; group?: string }>;
  functional?: FunctionalKind | string;
  functionalAt?: number;
  esterAlkyl?: string;
  formula?: string;
  name?: string;
  title?: string;
  cue_steps?: unknown;
}

interface Props {
  config?: SkeletalStructureConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

// ── Geometry ────────────────────────────────────────────────────────────────
const W = 360;
const H = 200;
const SEG = 40; // length of one backbone segment (horizontal advance per bond)
const RISE = 22; // vertical zig-zag amplitude
const DOUBLE_GAP = 4; // separation of the two lines in a double bond
const TRIPLE_GAP = 5; // separation of the outer lines in a triple bond
const MAX_C = 12; // clamp the main chain so it always fits the viewBox
const MIN_RING = 3; // smallest ring we draw (cyclopropane)
const MAX_RING = 8; // largest ring we draw cleanly in the viewBox
const RING_R = 56; // ring radius (regular polygon circumradius)

// Hetero-atom color pulled from the shared palette so O reads red.
const O_COLOR = elementColor('O');

// A backbone vertex: a carbon at an absolute (x,y). Pure-carbon vertices draw no
// glyph (skeletal convention); we still keep the coordinate for bonds/branches.
interface Vertex {
  x: number;
  y: number;
}

// A resolved bond between consecutive backbone carbons.
interface Bond {
  i: number; // index of the start carbon (0-based), bond runs i → j
  j: number; // index of the end carbon (0-based); = i+1 for chains, wraps to 0 for the ring's closing edge
  order: 1 | 2 | 3;
  closing?: boolean; // true for the ring edge that joins the last carbon back to the first
}

// Normalize + clamp the carbon count. Rings get clamped to [MIN_RING, MAX_RING]
// so the polygon stays legible; chains keep their original [1, MAX_C] range.
function resolveCarbons(n: number | undefined, cyclic = false): number {
  const c = Math.round(Number.isFinite(n) ? (n as number) : 6);
  if (cyclic) {
    if (!Number.isFinite(c)) return MIN_RING;
    return Math.min(MAX_RING, Math.max(MIN_RING, c));
  }
  if (!Number.isFinite(c) || c < 1) return 6;
  return Math.min(MAX_C, Math.max(1, c));
}

// Build the per-bond order list (default order 1) from the bonds config,
// ignoring out-of-range / malformed entries rather than throwing.
// Chains have `carbons - 1` edges; rings have `carbons` edges (the last closes
// carbon N back to carbon 1). `between` is 1-indexed in both cases.
function resolveBonds(
  carbons: number,
  bonds: SkeletalStructureConfig['bonds'],
  cyclic = false,
): Bond[] {
  const edgeCount = cyclic
    ? Math.max(0, carbons >= MIN_RING ? carbons : 0)
    : Math.max(0, carbons - 1);
  const orders: (1 | 2 | 3)[] = new Array(edgeCount).fill(1);
  if (Array.isArray(bonds)) {
    for (const b of bonds) {
      const between = Math.round(Number(b?.between));
      const ord = Math.round(Number(b?.order));
      // `between` is 1-indexed: edge between carbon `between` and the next.
      const idx = between - 1;
      if (Number.isFinite(idx) && idx >= 0 && idx < orders.length) {
        orders[idx] = (ord === 2 ? 2 : ord === 3 ? 3 : 1) as 1 | 2 | 3;
      }
    }
  }
  return orders.map((order, i) => {
    const closing = cyclic && i === edgeCount - 1;
    return { i, j: closing ? 0 : i + 1, order, closing };
  });
}

// Lay the backbone out as a horizontal zig-zag centered in the viewBox. The
// chain auto-scales its segment length down if it would overflow horizontally.
function layoutBackbone(carbons: number): { vertices: Vertex[]; seg: number } {
  const n = Math.max(1, carbons);
  const marginX = 46; // room for terminal glyphs (OH / CHO / COOH)
  const usableW = W - marginX * 2;
  const seg = n > 1 ? Math.min(SEG, usableW / (n - 1)) : SEG;
  const totalW = (n - 1) * seg;
  const x0 = (W - totalW) / 2;
  const cy = H / 2 + 6;
  const vertices: Vertex[] = [];
  for (let i = 0; i < n; i++) {
    const up = i % 2 === 0; // alternate up/down for the zig-zag
    vertices.push({ x: x0 + i * seg, y: cy + (up ? -RISE / 2 : RISE / 2) });
  }
  return { vertices, seg };
}

// Lay the carbons out as a regular polygon (closed ring) centered in the
// viewBox. Vertex 0 sits at the top; vertices proceed clockwise. The radius
// shrinks for larger rings so substituent labels still fit the canvas.
function layoutRing(carbons: number): {
  vertices: Vertex[];
  seg: number;
  center: Vertex;
  radius: number;
} {
  const n = Math.max(MIN_RING, carbons);
  const center = { x: W / 2, y: H / 2 + 2 };
  // Smaller circumradius for bigger rings so outward labels stay on-canvas.
  const radius = Math.max(34, RING_R - Math.max(0, n - 6) * 4);
  const vertices: Vertex[] = [];
  // Start at the top (−90°) and go clockwise.
  for (let i = 0; i < n; i++) {
    const ang = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    vertices.push({
      x: center.x + radius * Math.cos(ang),
      y: center.y + radius * Math.sin(ang),
    });
  }
  // Approximate edge length (used to scale functional-group bond lengths).
  const seg = 2 * radius * Math.sin(Math.PI / n);
  return { vertices, seg, center, radius };
}

// Unit perpendicular to a segment (for offsetting the extra double/triple lines).
function perp(a: Vertex, b: Vertex): { px: number; py: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  return { px: -dy / len, py: dx / len };
}

// Pretty molecular formula with subscript digits (visual only).
function prettyFormula(f: string): string {
  return f.replace(/(\d+)/g, (m) =>
    m
      .split('')
      .map((d) => '₀₁₂₃₄₅₆₇₈₉'[Number(d)])
      .join(''),
  );
}

// Canonical alkane/ene/yne formula when the author gives no explicit formula.
// Counts H on the main chain (CnH2n+2 baseline), then applies degree of
// unsaturation from double/triple bonds and a best-effort functional-group
// adjustment. This is a teaching label, kept honest for the common cases.
function deriveFormula(
  carbons: number,
  bonds: Bond[],
  functional: FunctionalKind,
  cyclic = false,
): string {
  let c = carbons;
  // A ring closure is itself one degree of unsaturation: CnH2n vs CnH2n+2.
  let h = 2 * carbons + 2 - (cyclic ? 2 : 0);
  // Each unit of unsaturation removes 2 H.
  for (const b of bonds) h -= 2 * (b.order - 1);
  let o = 0;
  switch (functional) {
    case 'alcohol':
      o += 1; // replace one C–H with C–OH: net +O, H unchanged
      break;
    case 'ether':
      o += 1; // insert –O– into chain
      break;
    case 'aldehyde':
      o += 1;
      h -= 0; // terminal CHO: C already counted, =O adds O, loses 1 H vs CH3 but +O
      break;
    case 'ketone':
      o += 1;
      h -= 2; // C=O in chain removes 2 H
      break;
    case 'carboxylic-acid':
      o += 2;
      h -= 0; // –COOH on a terminal carbon
      break;
    case 'ester':
      o += 2;
      h -= 2;
      break;
    default:
      break;
  }
  if (h < 0) h = 0;
  const parts = [`C${c <= 0 ? '' : c}`, h > 0 ? `H${h}` : '', o > 0 ? `O${o > 1 ? o : ''}` : ''];
  return parts.filter(Boolean).join('');
}

const FUNCTIONAL_SET = new Set<FunctionalKind>([
  'alcohol',
  'ether',
  'aldehyde',
  'ketone',
  'carboxylic-acid',
  'ester',
  'none',
]);

function resolveFunctional(f: SkeletalStructureConfig['functional']): FunctionalKind {
  const k = String(f ?? 'none').toLowerCase().trim() as FunctionalKind;
  return FUNCTIONAL_SET.has(k) ? k : 'none';
}

const FUNCTIONAL_LABEL: Record<FunctionalKind, string> = {
  alcohol: 'Alcohol (–OH)',
  ether: 'Ether (–O–)',
  aldehyde: 'Aldehyde (–CHO)',
  ketone: 'Ketone (C=O)',
  'carboxylic-acid': 'Carboxylic acid (–COOH)',
  ester: 'Ester (–COO–)',
  none: '',
};

export default function SkeletalStructure({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  const cyclic = config.cyclic === true;
  const carbons = useMemo(
    () => resolveCarbons(config.carbons, cyclic),
    [config.carbons, cyclic],
  );
  const bonds = useMemo(
    () => resolveBonds(carbons, config.bonds, cyclic),
    [carbons, config.bonds, cyclic],
  );
  const functional = useMemo(() => resolveFunctional(config.functional), [config.functional]);

  // Functional-group carbon (1-indexed in config → 0-indexed, clamped).
  const fnAt = useMemo(() => {
    const raw = Math.round(Number(config.functionalAt));
    const def = 1; // default to the terminal (first) carbon
    const oneIdx = Number.isFinite(raw) && raw >= 1 ? raw : def;
    return Math.min(carbons, Math.max(1, oneIdx)) - 1; // 0-based
  }, [config.functionalAt, carbons]);

  // Lay out the carbons either as a horizontal zig-zag (chain) or a regular
  // polygon (ring). `center`/`radius` are only meaningful for rings; for chains
  // we keep `center=null` and fall back to the existing up/down branch logic.
  const layout = useMemo(
    () => (cyclic ? layoutRing(carbons) : layoutBackbone(carbons)),
    [carbons, cyclic],
  );
  const vertices = layout.vertices;
  const seg = layout.seg;
  const ringCenter: Vertex | null =
    'center' in layout ? (layout.center as Vertex) : null;

  // Unit vector pointing OUTWARD from a vertex (away from the chain/ring), used
  // to place substituents and functional groups so they never overlap the body.
  // Rings → radially out from the center; chains → straight up/down opposite the
  // local zig-zag direction (preserving the original chain behaviour).
  const outwardDir = useMemo(() => {
    return (i: number): { ox: number; oy: number } => {
      const v = vertices[i];
      if (!v) return { ox: 0, oy: -1 };
      if (ringCenter) {
        const dx = v.x - ringCenter.x;
        const dy = v.y - ringCenter.y;
        const len = Math.hypot(dx, dy) || 1;
        return { ox: dx / len, oy: dy / len };
      }
      // Chain: branch points opposite the zig-zag (up for even index here, to
      // match the original substituent code which used `up = at % 2 === 0`).
      const up = i % 2 === 0;
      return { ox: 0, oy: up ? 1 : -1 };
    };
  }, [vertices, ringCenter]);

  // Substituents: branch lines off a vertex. Ignore out-of-range entries.
  // When two or more substituents share the same `at` (gem-dimethyl etc.) we tag
  // each with its position in the cluster (`splayIndex`/`splayCount`) so the
  // renderer can fan them apart and keep every label visible.
  const substituents = useMemo(() => {
    if (!Array.isArray(config.substituents)) return [];
    const cleaned = config.substituents
      .map((s) => ({ at: Math.round(Number(s?.at)) - 1, group: String(s?.group ?? '').trim() }))
      .filter((s) => s.group && Number.isFinite(s.at) && s.at >= 0 && s.at < carbons);
    const counts = new Map<number, number>();
    for (const s of cleaned) counts.set(s.at, (counts.get(s.at) ?? 0) + 1);
    const seen = new Map<number, number>();
    return cleaned.map((s) => {
      const splayCount = counts.get(s.at) ?? 1;
      const splayIndex = seen.get(s.at) ?? 0;
      seen.set(s.at, splayIndex + 1);
      return { ...s, splayIndex, splayCount };
    });
  }, [config.substituents, carbons]);

  // Ester off-oxygen alkyl label (e.g. 'propyl'); blank → generic CH3 cap.
  const esterAlkyl = useMemo(
    () => String(config.esterAlkyl ?? '').trim(),
    [config.esterAlkyl],
  );

  const formula = useMemo(() => {
    const explicit = (config.formula ?? '').trim();
    return explicit || deriveFormula(carbons, bonds, functional, cyclic);
  }, [config.formula, carbons, bonds, functional, cyclic]);

  // Total "reveal units" = backbone vertices (for the left→right draw / stepper).
  const totalUnits = carbons;

  // revealed = how many backbone carbons (and the bonds before them) are drawn.
  const [revealed, setRevealed] = useState<number>(reduced || hasCue ? totalUnits : 0);
  const [showTry, setShowTry] = useState(false);
  const interactedRef = useRef(false);
  const timersRef = useRef<number[]>([]);

  const clearTimers = () => {
    timersRef.current.forEach((t) => window.clearTimeout(t));
    timersRef.current = [];
  };

  const markInteracted = () => {
    setShowTry(false);
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'skeletal_structure', carbons, functional, formula });
    }
  };

  // Self-demo: draw the skeleton one carbon at a time, then offer NowYouTry.
  useEffect(() => {
    clearTimers();
    if (reduced || hasCue) {
      setRevealed(totalUnits);
      return;
    }
    setRevealed(0);
    for (let i = 1; i <= totalUnits; i++) {
      timersRef.current.push(window.setTimeout(() => setRevealed(i), 260 * i));
    }
    timersRef.current.push(
      window.setTimeout(() => setShowTry(true), 260 * (totalUnits + 1)),
    );
    return clearTimers;
    // Re-arm when the molecule shape changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carbons]);

  // Cue: progress animates the draw ONLY while actively playing; at rest (paused,
  // e.g. a student landing on the slide) show the FULL structure so the stage is
  // never blank. step reveals one carbon at a time.
  useEffect(() => {
    if (!cue) return;
    clearTimers();
    setShowTry(false);
    if (cue.play === true && typeof cue.progress === 'number') {
      const p = Math.max(0, Math.min(1, cue.progress));
      setRevealed(Math.round(p * totalUnits));
    } else if (cue.play === true && typeof cue.step === 'number') {
      setRevealed(Math.max(0, Math.min(totalUnits, Math.round(cue.step) + 1)));
    } else {
      // paused / no progress → full structure, never an empty canvas
      setRevealed(totalUnits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue, totalUnits]);

  // Spotlight: 'double' | 'triple' | 'functional' | a carbon index/number.
  const spotlight = cueHighlights(cue);
  const litFeature = (kind: 'double' | 'triple' | 'functional') => spotlight.includes(kind);
  // Carbon index highlight: accept "3", "c3", "carbon 3" (1-indexed).
  const litCarbon = useMemo(() => {
    for (const s of spotlight) {
      const m = s.match(/(\d+)/);
      if (m) return parseInt(m[1], 10) - 1; // 0-based
    }
    return -1;
  }, [spotlight]);
  const anyHighlight = spotlight.length > 0;

  const fullyDrawn = revealed >= totalUnits;

  const replay = () => {
    markInteracted();
    clearTimers();
    if (reduced) {
      setRevealed(totalUnits);
      return;
    }
    setRevealed(0);
    for (let i = 1; i <= totalUnits; i++) {
      timersRef.current.push(window.setTimeout(() => setRevealed(i), 200 * i));
    }
  };

  // A backbone bond is drawn once BOTH its carbons are revealed. The ring's
  // closing edge (last carbon → carbon 1) only appears once every carbon is in.
  const bondVisible = (b: Bond) =>
    b.closing ? revealed >= carbons : revealed >= b.i + 2;
  // A vertex is drawn once revealed.
  const vertexVisible = (i: number) => revealed >= i + 1;

  // Dim helper for non-highlighted geometry when a spotlight is on.
  const dimUnless = (lit: boolean) => (anyHighlight && !lit ? 0.25 : 1);

  // Terminal anchor used to attach a functional group on a given vertex.
  //  - dir: legacy horizontal hint (+1 right, -1 left, 0 in-chain) for CHAINS.
  //  - ux/uy: unit vector pointing OUT (the canonical placement direction). For
  //    chains this is the horizontal `dir` (or straight up for in-chain carbons);
  //    for rings it is the radial outward direction. The functional renderer uses
  //    ux/uy plus its perpendicular so every group works on chains AND rings.
  //  - sx/sy: unit vector 90° from outward (the "side" used for the second arm
  //    of –COOH / esters and for offsetting the carbonyl =O).
  const groupAnchor = (
    i: number,
  ): { x: number; y: number; dir: number; ux: number; uy: number; sx: number; sy: number } => {
    const v = vertices[i] ?? { x: W / 2, y: H / 2 };
    if (ringCenter) {
      const { ox, oy } = outwardDir(i);
      // perpendicular (side) direction
      return { x: v.x, y: v.y, dir: ox >= 0 ? 1 : -1, ux: ox, uy: oy, sx: -oy, sy: ox };
    }
    // Chain: terminal carbons point horizontally outward; in-chain points up.
    const dir = i === 0 ? -1 : i === carbons - 1 ? 1 : 0;
    if (dir === 0) {
      // in-chain (ketone): outward is straight up, side is horizontal
      return { x: v.x, y: v.y, dir: 0, ux: 0, uy: -1, sx: 1, sy: 0 };
    }
    return { x: v.x, y: v.y, dir, ux: dir, uy: 0, sx: 0, sy: -1 };
  };

  const fnLabel = FUNCTIONAL_LABEL[functional];

  return (
    <VizCard
      eyebrow="Organic chemistry · Skeletal (line-angle) structure"
      title={config.title ?? config.name ?? 'Read the carbon skeleton'}
      subtitle={
        <span>
          Each vertex &amp; line-end is a carbon · hydrogens are implied
          {config.name ? (
            <span className="text-ws-ink-2"> · {config.name}</span>
          ) : null}
        </span>
      }
      reducedMotion={reduced}
      hint="Double bonds draw two parallel lines, triple bonds three; hetero-atoms (O) are labelled"
      actions={
        <button
          type="button"
          onClick={replay}
          className={VIZ_BTN_GHOST}
          aria-label="Redraw the structure"
        >
          <RotateCcw size={12} /> Redraw
        </button>
      }
    >
      <div className="relative w-full">
        <NowYouTry show={showTry && !reduced && !hasCue} />

        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full rounded-md border border-ws-border bg-ws-surface-2"
          style={{ maxHeight: 240 }}
          role="img"
          aria-label={`Skeletal structure of ${formula}${config.name ? `, ${config.name}` : ''}`}
        >
          {/* ── Backbone bonds (chain segments or ring edges) ──────────── */}
          {bonds.map((b) => {
            if (!bondVisible(b)) return null;
            const a = vertices[b.i];
            const c = vertices[b.j];
            if (!a || !c) return null;
            const { px, py } = perp(a, c);
            const lit =
              (b.order === 2 && litFeature('double')) ||
              (b.order === 3 && litFeature('triple')) ||
              litCarbon === b.i ||
              litCarbon === b.j;
            const op = dimUnless(lit);
            const stroke = lit && anyHighlight ? 'var(--ws-accent)' : 'var(--ws-ink)';
            const sw = lit && anyHighlight ? 2.4 : 2;

            if (b.order === 1) {
              return (
                <line
                  key={`bond-${b.i}`}
                  x1={a.x}
                  y1={a.y}
                  x2={c.x}
                  y2={c.y}
                  stroke={stroke}
                  strokeWidth={sw}
                  strokeLinecap="round"
                  opacity={op}
                />
              );
            }
            if (b.order === 2) {
              // On a RING, draw the second line as a single INNER parallel line
              // (offset toward the ring center) and slightly shortened — the
              // classic cyclo-ene look. On a chain, keep the symmetric two lines.
              if (ringCenter) {
                const mid = { x: (a.x + c.x) / 2, y: (a.y + c.y) / 2 };
                // Choose the perpendicular sign that points toward the center.
                const sign =
                  px * (ringCenter.x - mid.x) + py * (ringCenter.y - mid.y) >= 0 ? 1 : -1;
                const o = DOUBLE_GAP + 1;
                const t = 0.16; // trim each end so the inner line sits within the edge
                const ix1 = a.x + (c.x - a.x) * t + px * o * sign;
                const iy1 = a.y + (c.y - a.y) * t + py * o * sign;
                const ix2 = c.x - (c.x - a.x) * t + px * o * sign;
                const iy2 = c.y - (c.y - a.y) * t + py * o * sign;
                return (
                  <g key={`bond-${b.i}`} opacity={op}>
                    <line x1={a.x} y1={a.y} x2={c.x} y2={c.y} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
                    <line x1={ix1} y1={iy1} x2={ix2} y2={iy2} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
                  </g>
                );
              }
              const o = DOUBLE_GAP / 2;
              return (
                <g key={`bond-${b.i}`} opacity={op}>
                  <line x1={a.x + px * o} y1={a.y + py * o} x2={c.x + px * o} y2={c.y + py * o} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
                  <line x1={a.x - px * o} y1={a.y - py * o} x2={c.x - px * o} y2={c.y - py * o} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
                </g>
              );
            }
            // triple
            const o = TRIPLE_GAP;
            return (
              <g key={`bond-${b.i}`} opacity={op}>
                <line x1={a.x} y1={a.y} x2={c.x} y2={c.y} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
                <line x1={a.x + px * o} y1={a.y + py * o} x2={c.x + px * o} y2={c.y + py * o} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
                <line x1={a.x - px * o} y1={a.y - py * o} x2={c.x - px * o} y2={c.y - py * o} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
              </g>
            );
          })}

          {/* ── Substituent branches ───────────────────────────────────── */}
          {substituents.map((s, k) => {
            if (!vertexVisible(s.at)) return null;
            const v = vertices[s.at];
            if (!v) return null;
            // Base outward direction (radial for rings, up/down for chains).
            const { ox, oy } = outwardDir(s.at);
            const baseAng = Math.atan2(oy, ox);
            // SPLAY: when several substituents share this carbon, fan them around
            // the outward direction so every label stays clear of the others.
            const FAN = Math.PI / 4.5; // ~40° between adjacent labels
            const offset =
              s.splayCount > 1 ? (s.splayIndex - (s.splayCount - 1) / 2) * FAN : 0;
            const ang = baseAng + offset;
            const dx = Math.cos(ang);
            const dy = Math.sin(ang);
            const len = RISE; // branch stub length
            const bx = v.x + dx * len;
            const by = v.y + dy * len;
            const lit = litCarbon === s.at;
            const op = dimUnless(lit);
            // Push the label a little further along the branch and nudge its
            // baseline so it never sits on top of the stub end.
            const lx = v.x + dx * (len + 9);
            const ly = v.y + dy * (len + 9) + (dy >= 0 ? 4 : 0);
            return (
              <g key={`sub-${s.at}-${k}`} opacity={op}>
                <line x1={v.x} y1={v.y} x2={bx} y2={by} stroke="var(--ws-ink)" strokeWidth={2} strokeLinecap="round" />
                <text
                  x={lx}
                  y={ly}
                  textAnchor="middle"
                  fontSize="11"
                  fontWeight="600"
                  fill="var(--ws-ink-2)"
                >
                  {prettyFormula(s.group)}
                </text>
              </g>
            );
          })}

          {/* ── Functional group ───────────────────────────────────────── */}
          {functional !== 'none' &&
            fullyDrawn &&
            (() => {
              const fnLit = litFeature('functional') || litCarbon === fnAt;
              const op = dimUnless(fnLit);
              const accent = fnLit && anyHighlight;
              const ink = accent ? 'var(--ws-accent)' : 'var(--ws-ink)';
              const anchor = groupAnchor(fnAt);
              const { x, y, dir, ux, uy, sx, sy } = anchor;
              const out = dir === 0 ? 0 : dir * (seg * 0.7);
              const tx = x + out;

              // Helper to draw a labelled hetero glyph.
              const oGlyph = (gx: number, gy: number, label: string) => (
                <text x={gx} y={gy} textAnchor="middle" dominantBaseline="middle" fontSize="13" fontWeight="700" fill={O_COLOR}>
                  {label}
                </text>
              );

              // ── RING functional groups ──────────────────────────────────
              // On a ring every group is built from the OUTWARD unit vector
              // (ux,uy) and its side (sx,sy), so the group always points away
              // from the ring center regardless of which vertex it's on.
              if (ringCenter) {
                const L = 26; // base arm length out from the ring vertex
                const GAP = DOUBLE_GAP / 2;
                const px = sx; // perpendicular = side vector
                const py = sy;

                if (functional === 'alcohol') {
                  const ex = x + ux * L;
                  const ey = y + uy * L;
                  return (
                    <g opacity={op}>
                      <line x1={x} y1={y} x2={ex} y2={ey} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                      {oGlyph(ex + ux * 9, ey + uy * 9, 'OH')}
                    </g>
                  );
                }
                if (functional === 'ether') {
                  const ex = x + ux * L;
                  const ey = y + uy * L;
                  const cx2 = ex + ux * L;
                  const cy2 = ey + uy * L;
                  return (
                    <g opacity={op}>
                      <line x1={x} y1={y} x2={ex - ux * 8} y2={ey - uy * 8} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                      {oGlyph(ex, ey, 'O')}
                      <line x1={ex + ux * 8} y1={ey + uy * 8} x2={cx2} y2={cy2} stroke="var(--ws-ink)" strokeWidth={2} strokeLinecap="round" />
                    </g>
                  );
                }
                if (functional === 'aldehyde' || functional === 'ketone') {
                  // C=O pointing straight out from the ring vertex.
                  const ex = x + ux * L;
                  const ey = y + uy * L;
                  return (
                    <g opacity={op}>
                      <line x1={x + px * GAP} y1={y + py * GAP} x2={ex + px * GAP} y2={ey + py * GAP} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                      <line x1={x - px * GAP} y1={y - py * GAP} x2={ex - px * GAP} y2={ey - py * GAP} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                      {oGlyph(ex + ux * 9, ey + uy * 9, 'O')}
                    </g>
                  );
                }
                if (functional === 'carboxylic-acid') {
                  // –COOH: =O along outward, –OH off to the side.
                  const ex = x + ux * L;
                  const ey = y + uy * L;
                  const ohx = x + ux * (L * 0.4) + sx * L;
                  const ohy = y + uy * (L * 0.4) + sy * L;
                  return (
                    <g opacity={op}>
                      <line x1={x + px * GAP} y1={y + py * GAP} x2={ex + px * GAP} y2={ey + py * GAP} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                      <line x1={x - px * GAP} y1={y - py * GAP} x2={ex - px * GAP} y2={ey - py * GAP} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                      {oGlyph(ex + ux * 9, ey + uy * 9, 'O')}
                      <line x1={x} y1={y} x2={ohx - sx * 10} y2={ohy - sy * 10} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                      {oGlyph(ohx, ohy, 'OH')}
                    </g>
                  );
                }
                if (functional === 'ester') {
                  // –C(=O)–O–R: carbonyl =O outward, ester O to the side, then R.
                  const ex = x + ux * L; // carbonyl O direction
                  const ey = y + uy * L;
                  const oox = x + ux * (L * 0.4) + sx * L; // ester O
                  const ooy = y + uy * (L * 0.4) + sy * L;
                  const rx = oox + sx * 22; // off-O alkyl
                  const ry = ooy + sy * 22;
                  return (
                    <g opacity={op}>
                      <line x1={x + px * GAP} y1={y + py * GAP} x2={ex + px * GAP} y2={ey + py * GAP} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                      <line x1={x - px * GAP} y1={y - py * GAP} x2={ex - px * GAP} y2={ey - py * GAP} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                      {oGlyph(ex + ux * 9, ey + uy * 9, 'O')}
                      <line x1={x} y1={y} x2={oox - sx * 10} y2={ooy - sy * 10} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                      {oGlyph(oox, ooy, 'O')}
                      {esterAlkyl ? (
                        <>
                          <line x1={oox + sx * 10} y1={ooy + sy * 10} x2={rx} y2={ry} stroke="var(--ws-ink)" strokeWidth={2} strokeLinecap="round" />
                          <text x={rx + sx * 12} y={ry + sy * 12} textAnchor="middle" dominantBaseline="middle" fontSize="11" fontWeight="600" fill="var(--ws-ink-2)">
                            {prettyFormula(esterAlkyl)}
                          </text>
                        </>
                      ) : (
                        <line x1={oox + sx * 10} y1={ooy + sy * 10} x2={rx} y2={ry} stroke="var(--ws-ink)" strokeWidth={2} strokeLinecap="round" />
                      )}
                    </g>
                  );
                }
                return null;
              }

              if (functional === 'alcohol') {
                // –OH off the vertex (short bond out + "OH" label).
                const oy = y - RISE;
                return (
                  <g opacity={op}>
                    <line x1={x} y1={y} x2={x} y2={oy} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                    {oGlyph(x, oy - 8, 'OH')}
                  </g>
                );
              }

              if (functional === 'ether') {
                // –O– inserted past the terminal carbon, capped with a CH3.
                const ox = x + (dir || 1) * (seg * 0.55);
                const cx2 = ox + (dir || 1) * (seg * 0.55);
                const cy2 = y - RISE;
                return (
                  <g opacity={op}>
                    <line x1={x} y1={y} x2={ox} y2={y} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                    {oGlyph(ox, y, 'O')}
                    <line x1={ox + (dir || 1) * 8} y1={y} x2={cx2} y2={cy2} stroke="var(--ws-ink)" strokeWidth={2} strokeLinecap="round" opacity={accent ? op : 1} />
                  </g>
                );
              }

              if (functional === 'aldehyde') {
                // Terminal –CHO: the carbon has =O (double) + an implicit H.
                const ox = tx + (dir || 1) * (seg * 0.5);
                const oy = y - RISE;
                const { px, py } = perp({ x, y }, { x: ox, y: oy });
                const o2 = DOUBLE_GAP / 2;
                return (
                  <g opacity={op}>
                    {/* C=O double bond going up-out */}
                    <line x1={x + px * o2} y1={y + py * o2} x2={ox + px * o2} y2={oy + py * o2} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                    <line x1={x - px * o2} y1={y - py * o2} x2={ox - px * o2} y2={oy - py * o2} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                    {oGlyph(ox, oy - 8, 'O')}
                    {/* explicit terminal H */}
                    <text x={x + (dir || 1) * 12} y={y + 14} textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--ws-ink-3)">H</text>
                  </g>
                );
              }

              if (functional === 'ketone') {
                // In-chain C=O pointing straight up from the carbon.
                const oy = y - RISE - 4;
                const o2 = DOUBLE_GAP / 2;
                return (
                  <g opacity={op}>
                    <line x1={x - o2} y1={y} x2={x - o2} y2={oy} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                    <line x1={x + o2} y1={y} x2={x + o2} y2={oy} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                    {oGlyph(x, oy - 8, 'O')}
                  </g>
                );
              }

              if (functional === 'carboxylic-acid') {
                // –COOH: a carbon (vertex) with =O up and –OH out.
                const upx = x;
                const upy = y - RISE - 2;
                const ohx = x + (dir || 1) * (seg * 0.6);
                const o2 = DOUBLE_GAP / 2;
                return (
                  <g opacity={op}>
                    {/* C=O (double, up) */}
                    <line x1={upx - o2} y1={y} x2={upx - o2} y2={upy} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                    <line x1={upx + o2} y1={y} x2={upx + o2} y2={upy} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                    {oGlyph(upx, upy - 8, 'O')}
                    {/* C–OH (single, out) */}
                    <line x1={x} y1={y} x2={ohx - (dir || 1) * 10} y2={y} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                    {oGlyph(ohx, y, 'OH')}
                  </g>
                );
              }

              if (functional === 'ester') {
                // –C(=O)–O–R : carbonyl with =O up and –O– out to an alkyl R.
                // When `esterAlkyl` is given we draw that group on the far side
                // of the ester oxygen and label it, showing the full –C(=O)–O–R
                // instead of a generic CH3 cap.
                const d = dir || 1;
                const upx = x;
                const upy = y - RISE - 2;
                const ox = x + d * (seg * 0.55);
                const capx = ox + d * (seg * 0.55);
                const capy = y - RISE;
                const o2 = DOUBLE_GAP / 2;
                return (
                  <g opacity={op}>
                    <line x1={upx - o2} y1={y} x2={upx - o2} y2={upy} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                    <line x1={upx + o2} y1={y} x2={upx + o2} y2={upy} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                    {oGlyph(upx, upy - 8, 'O')}
                    <line x1={x} y1={y} x2={ox - d * 8} y2={y} stroke={ink} strokeWidth={2} strokeLinecap="round" />
                    {oGlyph(ox, y, 'O')}
                    <line x1={ox + d * 8} y1={y} x2={capx} y2={capy} stroke="var(--ws-ink)" strokeWidth={2} strokeLinecap="round" />
                    {esterAlkyl ? (
                      <text
                        x={capx + d * 4}
                        y={capy - 8}
                        textAnchor="middle"
                        fontSize="11"
                        fontWeight="600"
                        fill="var(--ws-ink-2)"
                      >
                        {prettyFormula(esterAlkyl)}
                      </text>
                    ) : null}
                  </g>
                );
              }
              return null;
            })()}

          {/* ── Carbon-index highlight ring (when a carbon is spotlighted) ── */}
          {litCarbon >= 0 &&
            litCarbon < carbons &&
            vertexVisible(litCarbon) && (
              <circle
                cx={vertices[litCarbon].x}
                cy={vertices[litCarbon].y}
                r={9}
                fill="none"
                stroke="var(--ws-accent)"
                strokeWidth={2}
                opacity={0.9}
              />
            )}

          {/* Subtle vertex dots while drawing, so the chain is legible as it
              grows (skeletal structures normally show no dot, so these fade out
              once a vertex has a neighbouring bond drawn). */}
          {vertices.map((v, i) =>
            vertexVisible(i) ? (
              <circle
                key={`v-${i}`}
                cx={v.x}
                cy={v.y}
                r={1.6}
                fill="var(--ws-ink-3)"
                opacity={fullyDrawn ? 0 : 0.8}
                style={{ transition: reduced ? undefined : 'opacity 300ms' }}
              />
            ) : null,
          )}
        </svg>

        {/* caption: formula + (optional) name + functional badge */}
        <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
          <span className="font-mono text-base font-semibold text-ws-ink">
            {prettyFormula(formula)}
          </span>
          {config.name && (
            <span className="text-sm text-ws-ink-2">{config.name}</span>
          )}
          {fnLabel && (
            <span
              className="inline-flex items-center gap-1 rounded-sm border border-ws-border px-1.5 py-0.5 text-[11px] text-ws-ink-2"
              style={{ borderColor: anyHighlight && litFeature('functional') ? 'var(--ws-accent)' : undefined }}
            >
              <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: O_COLOR }} />
              {fnLabel}
            </span>
          )}
        </div>

        {/* stepper: reveal the chain one carbon at a time by clicking */}
        <div className="mt-3 flex items-center gap-3">
          <span className="min-w-0 flex-shrink-0 text-[11px] font-medium text-ws-ink-2">
            {fullyDrawn
              ? `${carbons}-carbon ${cyclic ? 'ring' : 'chain'}`
              : `Drawing… ${revealed}/${carbons}`}
          </span>
          <input
            type="range"
            min={0}
            max={totalUnits}
            step={1}
            value={revealed}
            onChange={(e) => {
              setRevealed(Number(e.target.value));
              markInteracted();
            }}
            aria-label="Reveal the carbon skeleton"
            className="h-1 flex-1 cursor-pointer accent-ws-accent"
          />
        </div>
      </div>
    </VizCard>
  );
}
